import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * The central card holding the item to handle (phrase to read, audio to
 * listen to, text to check).
 *
 * ```tsx
 * <TaskItemCard label="À lire" corner={<EditButton />} footer={<TaskStepDots steps={steps} />}>
 *   <TaskItemText>{sentence}</TaskItemText>
 * </TaskItemCard>
 * ```
 *
 * - `label`: small caption above the content (what the item is).
 * - `corner`: one small action at the top right (edit, delete).
 * - `footer`: meta under a hairline (ids, counters, step dots).
 */
export function TaskItemCard({
  label,
  corner,
  footer,
  children,
  className,
}: {
  label?: ReactNode;
  corner?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("rounded-xl border border-border/60 bg-surface", className)}>
      <div className="space-y-4 p-5 sm:p-6">
        {label || corner ? (
          <div className="flex min-h-8 items-center justify-between gap-3">
            {label ? <p className="text-xs font-medium uppercase tracking-[0.08em] text-faint-foreground">{label}</p> : <span />}
            {corner}
          </div>
        ) : null}
        {children}
      </div>
      {footer ? <div className="border-t border-border/60 px-5 py-3 sm:px-6">{footer}</div> : null}
    </section>
  );
}

/** The phrase or text of the item, big and centred. Quotes are added. */
export function TaskItemText({ children, quoted = true, className }: { children: ReactNode; quoted?: boolean; className?: string }) {
  return (
    <p className={cn("py-4 text-center text-xl font-semibold leading-relaxed tracking-tight text-foreground sm:text-2xl", className)}>
      {quoted ? "“" : null}
      {children}
      {quoted ? "”" : null}
    </p>
  );
}

/** A labelled secondary text inside the card (expected French, translation...). */
export function TaskItemField({ label, children, className, testId }: { label: ReactNode; children: ReactNode; className?: string; testId?: string }) {
  return (
    <div className={cn("rounded-lg border border-border/60 bg-surface-2 px-4 py-3", className)}>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div data-testid={testId} className="mt-1 text-sm leading-6 text-foreground">
        {children}
      </div>
    </div>
  );
}
